import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Container } from "reactstrap";
import { downloadFileBlobCsv, getCustomerGroups, retrievePricesDownloadUrlByType } from "utils/api";
import { defaultNgiErrorMethod } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { FILTER_PAGE } from "utils/constants";
import useStoreFilters from "customHooks/useStoreFilters";
import { isEmpty } from "lodash";

import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import classname from "classnames";
import CustomerPriceGroupsFilter from "pages/price-management/CustomerPriceGroups/customerPriceGroupsFilter";
import { customerPriceGroupsListColumns } from "pages/price-management/CustomerPriceGroups/customerPriceGroupsListColumns";
import NewCustomerGroupModal from "pages/price-management/CustomerPriceGroups/newCustomerGroup/NewCustomerGroupModal";
import AddRemoveCustomersModal from "pages/price-management/CustomerPriceGroups/newCustomerGroup/AddRemoveCustomersModal";

const useStyles = makeStyles({
  filters: { display: "flex", justifyContent: "space-between" },
  overflowText: {
    overflow: "hidden",
    maxWidth: 110,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
});

const CustomerPriceGroups = () => {
  const classes = useStyles();
  const [isLoading, setLoader] = useState(false);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.CUSTOMER_PRICE_GROUPS);
  const [list, setList] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  useEffect(() => {
    handleFilter(!isEmpty(filters) ? filters : {});
  }, []);

  const handleFilter = useCallback((options = {}) => {
    setFilters(options);
    setLoader(true);
    getCustomerGroups(options)
      .then(res => {
        setList(res?.data || null);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const openNewCustomerGroup = useCallback(() => setActiveGroup({}));
  const closeNewCustomerGroup = useCallback(() => setActiveGroup(null));

  const openAddRemoveCustomers = useCallback(
    (item, type) =>
      setActiveItem({
        item,
        type,
      }),
    []
  );
  const closeAddRemoveCustomers = useCallback(() => setActiveItem(null), []);

  const downloadCustomers = useCallback(item => {
    setLoader(true);
    retrievePricesDownloadUrlByType("CustomerGroups", item.csvFilename)
      .then(res => {
        const {
          result: { url, fileName },
        } = res.data;
        downloadFileBlobCsv(url)
          .then(blob => {
            const url = window.URL.createObjectURL(new Blob([blob.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${fileName}`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
          })
          .catch(e => {
            defaultNgiErrorMethod(e);
          });
        setLoader(false);
      })
      .catch(e => {
        defaultNgiErrorMethod(e);
        setLoader(false);
      });
  }, []);

  const columns = useMemo(
    () => customerPriceGroupsListColumns(classes, setActiveGroup, openAddRemoveCustomers, downloadCustomers),
    []
  );

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className={classname(classes.filters, "filters-wrapper")}>
            <CustomButton
              icon={<i className="bx bx-plus-circle" />}
              type="primary"
              text="NEW CUSTOMER GROUP"
              onClick={openNewCustomerGroup}
            />
            <CustomerPriceGroupsFilter onFilter={handleFilter} isLoading={isLoading} />
          </div>
          <Col>
            <NGITable
              filtersType={FILTER_PAGE.CUSTOMER_PRICE_GROUPS}
              columns={columns}
              data={list}
              isLoading={isLoading}
              updateList={handleFilter}
            />
          </Col>
        </Container>
      </div>
      {activeGroup && (
        <NewCustomerGroupModal data={activeGroup} onClose={closeNewCustomerGroup} update={handleFilter} />
      )}
      {activeItem && (
        <AddRemoveCustomersModal data={activeItem} onClose={closeAddRemoveCustomers} update={handleFilter} />
      )}
    </>
  );
};

export default CustomerPriceGroups;
